/* ==========================================================================
   The WHYTE Ritual — live loyalty integration (Smile.js + Shopify context)

   Architecture:
     Shopify + official Smile app  ->  Smile.js already loaded and authenticated
                                   ->  this Hub consumes window.Smile
                                   ->  live customer, points, rewards, referrals

   There is no custom session endpoint, no Smile signing key and no second
   Smile.initialize() call. Shopify customer context (whyte.ritual_tier,
   deliveries, subscription) is supplied through window.WHYTE_RITUAL_CONFIG.shopify,
   while the birthday comes from Smile.customer.current().dateOfBirth.
   All copy lives in account.html.
   ========================================================================== */
(function () {
  "use strict";

  var CONFIG = window.WHYTE_RITUAL_CONFIG || {};

  /* Tier model — owned by Shopify (whyte.ritual_tier), never Smile VIP ----- */
  var TIERS = [
    {
      key: "unstarted",
      label: "Unstarted",
      multiplier: "0×",
      from: 0,
      to: 1,
      next: "Pure",
      copy: "Your WHYTE Ritual awaits.",
    },
    {
      key: "pure",
      label: "Pure",
      multiplier: "1×",
      from: 1,
      to: 4,
      next: "Ritual",
      copy: "Your Ritual is just beginning.",
    },
    {
      key: "ritual",
      label: "Ritual",
      multiplier: "1.5×",
      from: 4,
      to: 7,
      next: "Restore",
      copy: "Your consistency is paying off.",
    },
    {
      key: "restore",
      label: "Restore",
      multiplier: "2×",
      from: 7,
      to: 13,
      next: "Luminous",
      copy: "You're deep into your Ritual.",
    },
    {
      key: "luminous",
      label: "Luminous",
      multiplier: "2.5×",
      from: 13,
      to: null,
      next: null,
      copy: "You've unlocked the full Ritual experience.",
    },
  ];

  function tierByKey(key) {
    for (var i = 0; i < TIERS.length; i++) {
      if (TIERS[i].key === key) return TIERS[i];
    }
    return null;
  }

  /* Small DOM helpers ------------------------------------------------------ */
  function el(name) {
    return document.querySelector('[data-ritual="' + name + '"]');
  }
  function all(name) {
    return Array.prototype.slice.call(document.querySelectorAll('[data-ritual="' + name + '"]'));
  }
  function setText(name, value) {
    all(name).forEach(function (node) {
      node.textContent = value;
      node.classList.remove("wa-skel");
    });
  }
  function setState(node, state) {
    if (!node) return;
    node.setAttribute("data-state", state);
  }
  function show(node, visible) {
    if (node) node.hidden = !visible;
  }
  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (char) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char];
    });
  }
  function reveal(scope) {
    (scope || document).querySelectorAll("[data-reveal]").forEach(function (node) {
      node.classList.add("is-visible");
    });
  }

  function formatPoints(amount) {
    try {
      if (window.Smile && typeof window.Smile.formatPoints === "function") {
        return window.Smile.formatPoints(amount);
      }
    } catch (error) {
      /* fall through to the local formatter */
    }
    return Number(amount || 0).toLocaleString("en-US");
  }

  function formatDate(value) {
    if (!value) return "";
    var date = new Date(value);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  }

  /* ----------------------------------------------------------------------
     Rendering
     ---------------------------------------------------------------------- */
  var state = {
    balance: null,
    products: [],
    customer: null,
    shopify: null,
    tier: null,
    earningRules: [],
    birthdayAdded: false,
    referralSettings: null,
    rewardHistory: [],
  };

  function renderIntegrationError(error) {
    var banner = el("error");
    if (banner) {
      var copy = banner.querySelector("[data-ritual-error-copy]");
      if (copy) {
        copy.textContent =
          error && error.code === "customer_unavailable"
            ? "Sign in to your WHYTE account to see your Ritual."
            : "Your Ritual is taking a moment to appear. Please refresh, or try again shortly.";
      }
      banner.hidden = false;
    }
    renderBirthday("unavailable");
    document.querySelectorAll(".wa-skel").forEach(function (node) {
      node.classList.remove("wa-skel");
      node.textContent = node.getAttribute("data-empty") || "—";
    });
    if (error) console.error("[WHYTE Ritual]", error.code || "", error);
  }

  function renderCustomer(customer, shopify) {
    var firstName = (customer && customer.firstName) || (shopify && shopify.firstName) || null;
    setText("greeting", firstName ? "Welcome back, " + firstName : "Welcome back");
  }

  function renderTier(shopify) {
    var tier = tierByKey(shopify && shopify.ritualTier ? String(shopify.ritualTier).toLowerCase() : "");
    state.tier = tier;

    if (!tier) {
      setText("tier-eyebrow", "Your Ritual");
      setText("tier-name", "Your Ritual");
      setText("tier-copy", "Your Ritual tier will appear here once your subscription syncs.");
      setText("multiplier", "—");
      setText("multiplier-copy", "Your Ritual rewards consistency with every order.");
      return;
    }

    setText("tier-eyebrow", "Your Ritual - " + tier.label);
    setText("tier-name", tier.label);
    setText("tier-copy", tier.copy);
    setText("multiplier", tier.multiplier + " Points");
    setText("multiplier-copy", "Your Ritual rewards consistency with every order.");

    // Progress toward the next Ritual, from completed subscription deliveries.
    var deliveries = shopify ? shopify.completedDeliveries : null;
    var progressNode = el("progress");
    if (deliveries == null || tier.to == null) {
      setText(
        "progress-copy",
        tier.to == null
          ? "You've reached the final Ritual."
          : "Delivery progress will appear with your next subscription update.",
      );
      setText("progress-count", tier.to == null ? "Complete" : "—");
      if (progressNode) progressNode.style.setProperty("--wa-progress", tier.to == null ? "100%" : "0%");
    } else {
      var remaining = Math.max(tier.to - deliveries, 0);
      var span = tier.to - tier.from;
      var done = Math.min(Math.max(deliveries - tier.from, 0), span);
      var percent = span > 0 ? Math.round((done / span) * 100) : 100;
      setText("progress-count", deliveries + " of " + tier.to + " deliveries");
      setText(
        "progress-copy",
        remaining > 0
          ? remaining + (remaining === 1 ? " delivery until " : " deliveries until ") + tier.next
          : "Your next Ritual is unlocking.",
      );
      if (progressNode) progressNode.style.setProperty("--wa-progress", percent + "%");
    }

    setText("next-tier", tier.next || tier.label);

    // Highlight the live stage in the journey and levels sections.
    document.querySelectorAll("[data-ritual-stage]").forEach(function (node) {
      var key = node.getAttribute("data-ritual-stage");
      var index = TIERS.findIndex(function (item) {
        return item.key === key;
      });
      var current = TIERS.findIndex(function (item) {
        return item.key === tier.key;
      });
      node.classList.toggle("is-complete", index < current);
      node.classList.toggle("is-current", index === current);
      if (index === current) node.setAttribute("aria-current", "step");
      else node.removeAttribute("aria-current");
      var badge = node.querySelector("[data-ritual-current-badge]");
      if (badge) badge.hidden = index !== current;
    });
  }

  function renderSubscription(shopify) {
    var node = el("subscription");
    if (!node) return;
    var active = shopify ? shopify.subscriptionActive : null;
    node.classList.remove("wa-skel");
    if (active === true) {
      node.textContent = node.getAttribute("data-copy-active") || "Your Ritual is active";
      node.setAttribute("data-state", "active");
    } else if (active === false) {
      node.textContent = node.getAttribute("data-copy-inactive") || "Start your Ritual";
      node.setAttribute("data-state", "inactive");
    } else {
      node.textContent = node.getAttribute("data-copy-unknown") || "The WHYTE Ritual";
      node.setAttribute("data-state", "unknown");
    }
    var cta = el("subscription-cta");
    show(cta, active === false);
  }

  function renderBalance(balance) {
    state.balance = balance;
    setText("points", formatPoints(balance));
  }

  function rewardCard(product, balance) {
    var pointsProduct = product.pointsProduct || {};
    var reward = pointsProduct.reward || {};
    var price = product.pointsPrice;
    var locked = typeof price === "number" && balance != null && balance < price;
    var variable = pointsProduct.exchangeType === "variable";
    var description = reward.description || pointsProduct.exchangeDescription || "";
    var percent = locked && price > 0 ? Math.min(Math.round((balance / price) * 100), 100) : 100;
    var missing = locked ? price - balance : 0;

    var html = "";
    html += '<li class="wa-reward' + (locked ? " is-locked" : "") + '" data-reveal>';
    html += '<p class="wa-reward__points">' + escapeHtml(formatPoints(price)) + " Points</p>";
    html += '<h3 class="wa-reward__title">' + escapeHtml(product.name || reward.name || "Reward") + "</h3>";
    if (description) html += '<p class="wa-reward__body">' + escapeHtml(description) + "</p>";

    if (locked) {
      html += '<div class="wa-reward__progress">';
      html +=
        '<span class="wa-reward__track"><span class="wa-reward__fill" style="width:' + percent + '%"></span></span>';
      html += '<p class="wa-reward__state">' + escapeHtml(formatPoints(missing)) + " points to go</p>";
      html +=
        '<p class="wa-reward__count">' +
        escapeHtml(formatPoints(balance)) +
        " / " +
        escapeHtml(formatPoints(price)) +
        " points</p>";
      html += "</div>";
    } else {
      html += '<p class="wa-reward__cta">';
      html +=
        '<button class="wa-btn wa-btn--solid wa-btn--sm" type="button" data-redeem="' +
        escapeHtml(pointsProduct.id) +
        '"' +
        (variable ? ' data-points-to-spend="' + escapeHtml(price) + '"' : "") +
        ">Redeem Reward</button>";
      html += "</p>";
    }
    html += "</li>";
    return html;
  }

  function renderRewards(products, balance) {
    state.products = products || [];
    var list = el("rewards");
    if (!list) return;
    if (!state.products.length) {
      list.innerHTML =
        '<li class="wa-reward wa-reward--empty"><p class="wa-reward__body">' +
        escapeHtml(list.getAttribute("data-empty") || "New rewards are on their way.") +
        "</p></li>";
      return;
    }
    list.innerHTML = state.products
      .map(function (product) {
        return rewardCard(product, balance);
      })
      .join("");
    reveal(list);
  }

  function renderHistory(fulfillments) {
    var list = el("history");
    if (!list) return;
    var items = (fulfillments || []).filter(function (item) {
      return item && item.fulfillmentStatus !== "cancelled" && item.fulfillmentStatus !== "failed";
    });
    if (!items.length) {
      list.innerHTML =
        '<li class="wa-entry wa-entry--empty"><div class="wa-entry__text"><p class="wa-entry__date">' +
        escapeHtml(list.getAttribute("data-empty") || "Your redeemed rewards will appear here.") +
        "</p></div></li>";
      return;
    }
    list.innerHTML = items
      .slice(0, 8)
      .map(function (item, index) {
        var date = formatDate(item.createdAt);
        return (
          '<li class="wa-entry" data-reveal style="--wa-reveal-delay:' +
          index * 60 +
          'ms">' +
          '<div class="wa-entry__text">' +
          '<h3 class="wa-entry__title">' +
          escapeHtml(item.name || "Reward") +
          "</h3>" +
          (date ? '<p class="wa-entry__date">Redeemed ' + escapeHtml(date) + "</p>" : "") +
          "</div>" +
          (item.code ? '<p class="wa-entry__points">' + escapeHtml(item.code) + "</p>" : "") +
          "</li>"
        );
      })
      .join("");
    reveal(list);
  }

  /* Earning rules --------------------------------------------------------
     Smile owns every value here. We read the fields the SDK actually returns
     and fall back to nothing (never invented copy) when a field is absent. */
  function firstString() {
    for (var i = 0; i < arguments.length; i++) {
      var value = arguments[i];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  }

  function firstNumber() {
    for (var i = 0; i < arguments.length; i++) {
      var value = arguments[i];
      if (typeof value === "number" && isFinite(value)) return value;
      if (typeof value === "string" && value.trim() && isFinite(Number(value))) return Number(value);
    }
    return null;
  }

  function earningRuleCollection(payload) {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];

    var candidates = [
      payload.earningRules,
      payload.earning_rules,
      payload.rules,
      payload.items,
      payload.data,
      payload.results,
    ];

    for (var i = 0; i < candidates.length; i++) {
      if (Array.isArray(candidates[i])) return candidates[i];
      if (candidates[i] && Array.isArray(candidates[i].earningRules)) return candidates[i].earningRules;
      if (candidates[i] && Array.isArray(candidates[i].earning_rules)) return candidates[i].earning_rules;
    }

    return [];
  }

  /* Earning rules use the real Smile payload shape:
       rewardValue.type === "fixed"    -> rewardValue.fixed.value
       rewardValue.type === "variable" -> rewardValue.variable.{value,perAmount} */
  function earningPoints(rule) {
    var value = rule.rewardValue || {};

    if (value.type === "fixed" && value.fixed) {
      var fixed = firstNumber(value.fixed.value);
      if (fixed === null) return "";
      return "+" + formatPoints(fixed) + " " + (Math.abs(fixed) === 1 ? "Point" : "Points");
    }

    if (value.type === "variable" && value.variable) {
      var rate = firstNumber(value.variable.value);
      var per = firstNumber(value.variable.perAmount);
      if (rate === null) return "";
      var label = "+" + formatPoints(rate) + " " + (Math.abs(rate) === 1 ? "Point" : "Points");
      if (per === null) return label;
      return label + " per $" + formatPoints(per);
    }

    return "";
  }

  /* Smile does not return descriptions, so the Hub supplies concise copy for the
     configured actions. Unknown future rules simply render without a body. */
  var EARN_COPY = [
    {
      match: "review",
      body: "Share your experience by reviewing a WHYTE product.",
    },
    {
      match: "birthday",
      body: "Add your birthday to your WHYTE Ritual profile.",
      kind: "birthday",
    },
    {
      match: "order",
      body: "Earn points as you shop with WHYTE.",
      cta: { label: "Shop WHYTE", url: "/collections/subscriptions" },
    },
  ];

  function earningCopy(rule) {
    var name = String(rule.name || "").toLowerCase();
    for (var i = 0; i < EARN_COPY.length; i++) {
      if (name.indexOf(EARN_COPY[i].match) !== -1) return EARN_COPY[i];
    }
    return null;
  }

  function earningIcon(rule) {
    var url = firstString(rule.imageUrl);
    if (!url) return "";
    return (
      '<span class="wa-earn__icon" aria-hidden="true"><img src="' +
      escapeHtml(url) +
      '" alt="" loading="lazy" /></span>'
    );
  }

  function earningLimitNote(rule) {
    var limit = rule.earningLimit;
    if (!limit || limit.type !== "rolling" || !limit.rolling) return "";
    var count = firstNumber(limit.rolling.unitCount);
    // if (limit.rolling.unit === "day" && count !== null && count >= 365) {
    //   return "Once a year";
    // }
    return "";
  }

  function earningCta(rule, copy) {
    console.log("earningCta", rule, copy);
    var ordersCount = CONFIG.shopify ? CONFIG.shopify.ordersCount : 0;
    if (copy && copy.match === "review") {
      if (ordersCount === 0) {
        return '<p class="wa-earn__cta"><a class="wa-btn wa-btn--outline wa-btn--sm" href="/collections/subscriptions">Shop WHYTE</a></p>';
      }
      return '<p class="wa-earn__cta"><a class="wa-btn wa-btn--outline wa-btn--sm" href="/account/orders">Review Recent Orders</a></p>';
    }
    if (copy && copy.kind === "birthday") {
      if (state.birthdayAdded) {
        return '<p class="wa-earn__cta"><button class="wa-btn wa-btn--outline wa-btn--sm" type="button" data-ritual-earn-birthday>Update Your Birthday</button></p>';
      }
      return '<p class="wa-earn__cta"><button class="wa-btn wa-btn--outline wa-btn--sm" type="button" data-ritual-earn-birthday>Add Your Birthday</button></p>';
    }
    if (copy && copy.cta) {
      return (
        '<p class="wa-earn__cta"><a class="wa-btn wa-btn--outline wa-btn--sm" href="' +
        escapeHtml(copy.cta.url) +
        '">' +
        escapeHtml(copy.cta.label) +
        "</a></p>"
      );
    }
    return "";
  }

  function renderEarningRules(rules) {
    var list = el("earning-rules");
    if (!list) return;
    if (!rules || !rules.length) {
      list.innerHTML =
        '<li class="wa-earn__item wa-earn__item--empty"><p class="wa-earn__body">' +
        escapeHtml(list.getAttribute("data-empty") || "Ways to earn will appear here shortly.") +
        "</p></li>";
      return;
    }
    list.innerHTML = rules
      .map(function (rule, index) {
        rule = rule || {};
        var copy = earningCopy(rule);
        var points = earningPoints(rule);
        var note = earningLimitNote(rule);
        return (
          '<li class="wa-earn__item" data-reveal style="--wa-reveal-delay:' +
          index * 60 +
          'ms">' +
          earningIcon(rule) +
          '<h3 class="wa-earn__title">' +
          escapeHtml(firstString(rule.name) || "Earn with WHYTE") +
          "</h3>" +
          (points ? '<p class="wa-earn__points">' + escapeHtml(points) + "</p>" : "") +
          (copy && copy.body ? '<p class="wa-earn__body">' + escapeHtml(copy.body) + "</p>" : "") +
          (note ? '<p class="wa-earn__note">' + escapeHtml(note) + "</p>" : "") +
          earningCta(rule, copy) +
          "</li>"
        );
      })
      .join("");
    bindEarnBirthdayAction(list);
    reveal(list);
  }

  function bindEarnBirthdayAction(list) {
    var button = list.querySelector("[data-ritual-earn-birthday]");
    if (!button) return;
    button.addEventListener("click", function () {
      if (!openSmilePanel()) {
        toast("Your rewards panel is taking a moment to open. Please try again.", "error");
      }
    });
  }

  function findRule(rules, needle) {
    for (var i = 0; i < (rules || []).length; i++) {
      var rule = rules[i] || {};
      var haystack = String(rule.type || "") + " " + String(rule.name || "");
      if (haystack.toLowerCase().indexOf(needle) !== -1) return rule;
    }
    return null;
  }

  function renderRewardHighlights(rules) {
    var birthdayRule = findRule(rules, "birthday");
    var birthdayValue = birthdayRule ? earningPoints(birthdayRule) : "";
    if (birthdayValue) setText("birthday-reward", birthdayValue);
  }

  /* Referral rewards, straight from Smile.referralSettings.get().
     Rewards share the earning-rule payload shape, so the value is read from
     reward.rewardValue (fixed / variable) and falls back to the reward name. */
  function referralRewardLabel(reward) {
    if (!reward) return "";
    var value = earningPoints(reward);
    if (value) return value;
    return firstString(reward.name) || firstString(reward.displayName) || "";
  }

  function referralReward(settings, keys) {
    if (!settings) return null;
    for (var i = 0; i < keys.length; i++) {
      if (settings[keys[i]]) return settings[keys[i]];
    }
    return null;
  }

  function renderReferralRewards(settings) {
    var wrap = document.querySelector("[data-referral-rewards]");
    var sender = referralReward(settings, ["senderReward", "sender_reward", "referrerReward"]);
    var receiver = referralReward(settings, ["receiverReward", "receiver_reward", "friendReward"]);

    setText("referral-reward", referralRewardLabel(sender) || "Invite a friend and earn a reward.");
    setText("referral-friend-reward", referralRewardLabel(receiver) || "A welcome reward from WHYTE.");
    if (wrap) wrap.setAttribute("data-state", settings ? "ready" : "unavailable");
  }


  function renderReferral(customer) {
    var wrap = document.querySelector("[data-referral]");
    if (!wrap) return;
    var input = wrap.querySelector("[data-referral-input]");
    var url = customer && customer.referralUrl;
    show(wrap.querySelector("[data-referral-loading]"), false);
    if (!url) {
      setState(wrap, "unavailable");
      show(wrap.querySelector("[data-referral-unavailable]"), true);
      show(wrap.querySelector("[data-referral-live]"), false);
      return;
    }
    if (input) input.value = url;
    setState(wrap, "ready");
    show(wrap.querySelector("[data-referral-unavailable]"), false);
    show(wrap.querySelector("[data-referral-live]"), true);
  }

  /* ----------------------------------------------------------------------
     Birthday — Smile-sourced (Smile.customer.current().dateOfBirth).
     Isolated on purpose: a secure update mechanism can be attached to
     onBirthdayAction() later without touching the display logic.
     ---------------------------------------------------------------------- */
  var MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  /** Month + day only. Never the birth year. Returns null when unusable. */
  function formatBirthday(value) {
    if (value == null || value === "") return null;
    var raw = String(value).trim();
    if (!raw) return null;

    // Date-only strings are parsed manually so a timezone shift cannot move the day.
    var parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    var month;
    var day;
    if (parts) {
      month = Number(parts[2]);
      day = Number(parts[3]);
    } else {
      var date = new Date(raw);
      if (isNaN(date.getTime())) return null;
      month = date.getMonth() + 1;
      day = date.getDate();
    }
    if (!(month >= 1 && month <= 12) || !(day >= 1 && day <= 31)) return null;
    return MONTHS[month - 1] + " " + day;
  }

  function renderBirthday(status, value) {
    state.birthdayAdded = status === "ready" && !!formatBirthday(value);
    if (state.earningRules && state.earningRules.length) renderEarningRules(state.earningRules);
    var node = el("birthday");
    if (!node) return;
    var valueNode = node.querySelector("[data-ritual-birthday-value]");
    var emptyNode = node.querySelector("[data-ritual-birthday-empty]");
    var unavailableNode = node.querySelector("[data-ritual-birthday-unavailable]");
    var actionNode = node.querySelector("[data-ritual-birthday-action]");
    var unlockedNode = node.querySelector("[data-ritual-birthday-unlocked]");

    if (status === "loading") {
      setState(node, "loading");
      if (valueNode) valueNode.classList.add("wa-skel");
      show(valueNode, true);
      show(emptyNode, false);
      show(unlockedNode, false);
      show(unavailableNode, false);
      show(actionNode, false);
      return;
    }

    if (valueNode) valueNode.classList.remove("wa-skel");

    if (status === "unavailable") {
      // Still show the prominent invitation: the section must never look blank.
      setState(node, "empty");
      show(valueNode, false);
      show(emptyNode, true);
      show(unlockedNode, false);
      show(unavailableNode, true);
      if (actionNode) actionNode.textContent = "Add Your Birthday";
      show(actionNode, true);
      return;
    }

    var formatted = formatBirthday(value);
    show(unavailableNode, false);

    if (formatted) {
      setState(node, "ready");
      if (valueNode) valueNode.textContent = formatted;
      show(valueNode, true);
      show(emptyNode, false);
      show(unlockedNode, true);
      if (actionNode) actionNode.textContent = "Edit Birthday";
      show(actionNode, true);
    } else {
      setState(node, "empty");
      show(valueNode, false);
      show(unlockedNode, false);
      show(emptyNode, true);
      if (actionNode) actionNode.textContent = "Add Your Birthday";
      show(actionNode, true);
    }
  }

  /** Opens Smile's own customer-facing panel; the birthday stays managed by Smile. */
  function openSmilePanel() {
    var SmileUI = window.SmileUI;

    if (!SmileUI || typeof SmileUI.openPanel !== "function") {
      return false;
    }

    try {
      SmileUI.openPanel({
        deep_link: "points_activity_rules",
      });

      return true;
    } catch (error) {
      console.error("[WHYTE Ritual] could not open the Smile panel", error);
      return false;
    }
  }

  function bindBirthdayAction() {
    var node = el("birthday");
    if (!node) return;
    var actionNode = node.querySelector("[data-ritual-birthday-action]");
    if (!actionNode) return;
    actionNode.addEventListener("click", function () {
      // Smile owns the birthday. No custom write path, no API credentials in the
      // browser: we simply open Smile's native customer-facing panel.
      if (!openSmilePanel()) {
        toast("Your rewards panel is taking a moment to open. Please try again.", "error");
      }
    });
  }

  /* ----------------------------------------------------------------------
     Redemption
     ---------------------------------------------------------------------- */
  function toast(message, tone) {
    var node = el("toast");
    if (!node) return;
    node.textContent = message;
    node.setAttribute("data-tone", tone || "success");
    node.hidden = false;
    window.clearTimeout(node._timer);
    node._timer = window.setTimeout(function () {
      node.hidden = true;
    }, 4000);
  }

  /* ----------------------------------------------------------------------
     Smile resource loaders. Each resource is requested at most once per page
     load, cached in `state`, and fails independently: a rate limit on one
     resource never takes down another section.
     ---------------------------------------------------------------------- */
  function loadPointsWallet(Smile) {
    return Smile.customerPointsWallet
      .get()
      .then(function (wallet) {
        renderBalance(wallet ? wallet.balance : 0);
        // Rewards may have rendered before the balance arrived; re-render from
        // cached state so locked/available states are accurate. No refetch.
        if (state.products && state.products.length) renderRewards(state.products, state.balance);
      })
      .catch(function (error) {
        console.error("[WHYTE Ritual] points unavailable", error);
        setText("points", "—");
      });
  }

  function loadPointsProducts(Smile) {
    return Smile.customerPointsProducts
      .get()
      .then(function (products) {
        renderRewards(products, state.balance);
      })
      .catch(function (error) {
        console.error("[WHYTE Ritual] rewards unavailable", error);
        renderRewards([], state.balance);
      });
  }

  function loadRewardHistory(Smile) {
    return Smile.rewardFulfillments
      .get()
      .then(function (fulfillments) {
        state.rewardHistory = fulfillments || [];
        renderHistory(state.rewardHistory);
      })
      .catch(function (error) {
        console.error("[WHYTE Ritual] reward history unavailable", error);
        renderHistory([]);
      });
  }

  function fetchEarningRules(Smile) {
    return Promise.resolve().then(function () {
      if (!Smile.earningRules || typeof Smile.earningRules.get !== "function") {
        throw new Error("Smile.earningRules is unavailable");
      }
      return Smile.earningRules.get();
    });
  }

  function loadEarningRules(Smile) {
    function apply(rules) {
      state.earningRules = earningRuleCollection(rules);
      renderEarningRules(state.earningRules);
      renderRewardHighlights(state.earningRules);
    }

    return fetchEarningRules(Smile)
      .then(apply)
      .catch(function (error) {
        // Transient failure: keep the loading state and make ONE delayed retry.
        console.error("[WHYTE Ritual] earning rules failed, retrying once", error);
        return new Promise(function (resolve) {
          window.setTimeout(resolve, 1000);
        })
          .then(function () {
            return fetchEarningRules(Smile);
          })
          .then(apply)
          .catch(function (retryError) {
            console.error("[WHYTE Ritual] earning rules unavailable", retryError);
            renderEarningRules([]);
          });
      });
  }


  function fetchReferralSettings(Smile) {
    return Promise.resolve().then(function () {
      if (!Smile.referralSettings || typeof Smile.referralSettings.get !== "function") {
        throw new Error("Smile.referralSettings is unavailable");
      }
      return Smile.referralSettings.get();
    });
  }

  function loadReferralSettings(Smile) {
    function apply(settings) {
      state.referralSettings = settings || null;
      renderReferralRewards(state.referralSettings);
    }

    return fetchReferralSettings(Smile)
      .then(apply)
      .catch(function (error) {
        // Transient failure: keep the loading state and make ONE delayed retry.
        console.error("[WHYTE Ritual] referral settings failed, retrying once", error);
        return new Promise(function (resolve) {
          window.setTimeout(resolve, 1000);
        })
          .then(function () {
            return fetchReferralSettings(Smile);
          })
          .then(apply)
          .catch(function (retryError) {
            console.error("[WHYTE Ritual] referral settings unavailable", retryError);
            renderReferralRewards(null);
          });
      });
  }


  /* After a redemption only the balance, redeemable rewards and history can
     have changed. Earning rules and referral settings are left cached. */
  function refreshLoyalty(Smile) {
    return loadPointsWallet(Smile)
      .then(function () {
        return loadPointsProducts(Smile);
      })
      .then(function () {
        return loadRewardHistory(Smile);
      });
  }

  function bindRedemption(Smile) {
    document.addEventListener("click", function (event) {
      var button = event.target.closest("[data-redeem]");
      if (!button || button.disabled) return;
      var id = Number(button.getAttribute("data-redeem"));
      if (!id) return;

      var spend = button.getAttribute("data-points-to-spend");
      button.disabled = true;
      button.classList.add("is-busy");
      var original = button.textContent;
      button.textContent = "Redeeming";

      var options = spend ? { pointsToSpend: Number(spend) } : undefined;
      Smile.pointsProducts
        .purchase(id, options)
        .then(function () {
          toast("Your reward is on its way.", "success");
          return refreshLoyalty(Smile);
        })
        .catch(function (error) {
          console.error("[WHYTE Ritual] redemption failed", error);
          toast("That reward could not be redeemed just now.", "error");
          button.disabled = false;
          button.classList.remove("is-busy");
          button.textContent = original;
        });
    });
  }

  /* ----------------------------------------------------------------------
     Wait for the Smile.js instance the Shopify + Smile integration loads.
     We never load, initialize or authenticate a second copy of the SDK.
     Readiness is proven by a single successful Smile.customer.current();
     the resolved customer is cached and reused, never fetched twice.
     ---------------------------------------------------------------------- */
  function waitForSmile(timeoutMs) {
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      var timeout = timeoutMs || 15000;

      function check() {
        var Smile = window.Smile;

        if (Smile && Smile.customer && typeof Smile.customer.current === "function") {
          var pending;
          try {
            pending = Smile.customer.current();
          } catch (thrown) {
            pending = Promise.reject(thrown);
          }

          Promise.resolve(pending)
            .then(function (customer) {
              resolve({ Smile: Smile, customer: customer });
            })
            .catch(function (error) {
              var message = error && error.message ? String(error.message) : "";

              // Only the initialization error is retried.
              if (message.indexOf("Smile.js must be initialized before calling this method") !== -1) {
                if (Date.now() - started >= timeout) {
                  error.code = "smile_not_initialized";
                  reject(error);
                  return;
                }
                window.setTimeout(check, 150);
                return;
              }

              reject(error);
            });
          return;
        }

        if (Date.now() - started >= timeout) {
          var missing = new Error("Smile.js is not available on this page");
          missing.code = "smile_not_loaded";
          reject(missing);
          return;
        }

        window.setTimeout(check, 150);
      }

      check();
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function runSmileTasks(result) {
    var Smile = result.Smile;
    var customer = result.customer;

    bindRedemption(Smile);

    // The customer came from waitForSmile(); customer.current() is never
    // called a second time.
    state.customer = customer || null;
    renderCustomer(state.customer, state.shopify);
    renderReferral(state.customer);
    renderBirthday(state.customer ? "ready" : "unavailable", state.customer ? state.customer.dateOfBirth : null);

    // Resources load one after another so earning rules never compete with the
    // initial points/customer request burst (Smile rate limits).
    return loadPointsWallet(Smile)
      .then(function () {
        return loadPointsProducts(Smile);
      })
      .then(function () {
        return loadRewardHistory(Smile);
      })
      .then(function () {
        return loadEarningRules(Smile);
      })
      .then(function () {
        return loadReferralSettings(Smile);
      });
  }


  function start() {
    // Shopify-owned context renders immediately; it does not depend on Smile.
    state.shopify = CONFIG.shopify || null;
    renderTier(state.shopify);
    renderSubscription(state.shopify);
    renderBirthday("loading");
    bindBirthdayAction();

    waitForSmile(15000)
      .then(runSmileTasks)
      .catch(function (error) {
        renderIntegrationError(error);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
